'use client';

/**
 * useCartCouponForm - Hook for cart coupon form state management
 *
 * Performance-optimized: Uses FastCartTotals data (already fetched by useHybridCartPrices)
 * for coupon detection instead of making redundant API calls.
 *
 * Key optimizations:
 * - Coupon presence/removal detected from FastCartTotals.coupon_code (zero cost)
 * - MarketingGetAppliedCodes fetched only once (debounced) for voucher display details
 * - MarketingGetAppliedCodes only fetched for authenticated users (guest users skip)
 * - MarketingGetAppliedCodes re-fetched when selected items or quantities change
 * - Removed redundant useGetAppliedCouponsLazy (FastCartTotals has discount_amount)
 * - Removed redundant useCartTotalDetails (useHybridCartPrices already handles it)
 * - Uses useCartSyncStatus() internally when syncStatus prop is not provided
 *
 * Removal Policy:
 * - Coupons/vouchers are ONLY removed by explicit user action
 * - NO automatic removal on quantity change or server validation
 * - User must click remove button or apply different coupon to remove current one
 * - EXCEPTION: Auto-remove when ALL items are unchecked (selectedReferenceCount === 0)
 *   because a coupon on an empty selection is invalid and causes server errors
 *
 * @module @kidslabs/module-flash-sale/modules/sales-rule/hooks/use-cart-coupon-form
 */

import { useCart, useHybridCartPrices, useCartSyncStatus } from '@kidslabs/module-quote';
import { checkoutStoreAPI } from '@kidslabs/module-checkout';
import { useMarketingAppliedCodesLazy } from '../../../api';
import { MarketingCodeType } from '../../../types/marketing.graphql';
import { useRemoveCode } from './use-remove-code';
import type { SyncStatus } from '../components/cart-coupon-form/cart-coupon-form.types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { currencyFormatter, authStore$ } from '@kidslabs/module-core';

/** Debounce delay for marketing details refetch after cart sync (ms) */
const MARKETING_REFETCH_DEBOUNCE_MS = 500;

/** Maximum time to wait for server verification before auto-clearing pending state (ms) */
const PENDING_VERIFICATION_TIMEOUT_MS = 10_000;

/**
 * Web replacement for React Navigation's useIsFocused.
 * Tracks document visibility so queries pause when the tab is hidden.
 */
const useIsFocused = (): boolean => {
    const [isFocused, setIsFocused] = useState(true);

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsFocused(document.visibilityState === 'visible');
        };

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handleVisibilityChange);
            return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
    }, []);

    return isFocused;
};

interface UseCartCouponFormProps {
    syncStatus?: SyncStatus;
}

export const useCartCouponForm = ({ syncStatus: externalSyncStatus }: UseCartCouponFormProps) => {
    const isFocused = useIsFocused();
    const { cartId } = useCart();
    const isAuthenticated = authStore$.isAuthenticated.get() ?? false;

    const {
        selectedReferenceCount,
        selectedReferences,
        prices,
        serverCouponCode,
        serverDiscountAmount,
        isServerPrices,
        retryServerPrices,
    } = useHybridCartPrices();

    const internalSyncStatus = useCartSyncStatus();
    const syncStatus = externalSyncStatus ?? internalSyncStatus;

    const {
        fetchAppliedCodes,
        appliedVouchers,
        appliedCoupons,
        loading,
        error,
        refetch: refetchMarketingAppliedCode,
    } = useMarketingAppliedCodesLazy({ cartId });

    const { removeCode } = useRemoveCode();

    // ═══════════════════════════════════════════════════════════════════════
    // Local tracking of applied voucher/coupon for optimistic display
    // ═══════════════════════════════════════════════════════════════════════
    const localTrackedVoucherRef = useRef<string | null>(null);
    const appliedVouchersRef = useRef(appliedVouchers);
    appliedVouchersRef.current = appliedVouchers;

    const pendingServerVerificationRef = useRef(false);
    const pendingVerificationTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const prevMarketingVoucherIdRef = useRef<string | null | undefined>(undefined);
    const prevMarketingCouponCodeRef = useRef<string | null | undefined>(undefined);

    // Last confirmed coupon display as back buffer (prevents UI flicker during transitions)
    const lastConfirmedCouponDisplayRef = useRef<string>('');

    // ═══════════════════════════════════════════════════════════════════════
    // Detect new voucher/coupon applied → trigger server verification
    // ═══════════════════════════════════════════════════════════════════════
    useEffect(() => {
        const currentVoucherId = appliedVouchers?.[0]?.voucher_id ?? null;
        const currentCouponCode = appliedCoupons?.[0]?.code ?? null;

        if (currentVoucherId) {
            localTrackedVoucherRef.current = currentVoucherId;
        }

        const isNewVoucher = currentVoucherId && currentVoucherId !== prevMarketingVoucherIdRef.current;
        const isNewCoupon = currentCouponCode && currentCouponCode !== prevMarketingCouponCodeRef.current;

        if ((isNewVoucher || isNewCoupon) && cartId) {
            // Mark as pending verification (will be cleared when server confirms discount)
            pendingServerVerificationRef.current = true;

            // Safety timeout: auto-clear pending state if server never responds
            if (pendingVerificationTimeoutRef.current) {
                clearTimeout(pendingVerificationTimeoutRef.current);
            }
            pendingVerificationTimeoutRef.current = setTimeout(() => {
                pendingServerVerificationRef.current = false;
            }, PENDING_VERIFICATION_TIMEOUT_MS);

            // Trigger server cart totals refetch to get updated discount
            checkoutStoreAPI.actions.incrementDiscountVersion();

            // Force FastCartTotals refetch in useHybridCartPrices so
            // serverDiscountAmount updates (selection fingerprint did not change)
            retryServerPrices();
        }

        prevMarketingVoucherIdRef.current = currentVoucherId;
        prevMarketingCouponCodeRef.current = currentCouponCode;
    }, [appliedVouchers, appliedCoupons, cartId, retryServerPrices]);

    // ═══════════════════════════════════════════════════════════════════════
    // Clear pending verification flag when server confirms discount
    // ═══════════════════════════════════════════════════════════════════════
    useEffect(() => {
        if (!isServerPrices || !pendingServerVerificationRef.current) return;

        // Server prices arrived → clear pending regardless of discount result.
        // Whether server confirmed a discount or rejected the coupon, verification is complete.
        pendingServerVerificationRef.current = false;

        if (pendingVerificationTimeoutRef.current) {
            clearTimeout(pendingVerificationTimeoutRef.current);
            pendingVerificationTimeoutRef.current = undefined;
        }
    }, [isServerPrices, serverCouponCode, serverDiscountAmount]);

    const trackAppliedCode = useCallback((voucherId: string) => {
        localTrackedVoucherRef.current = voucherId;
    }, []);

    // ═══════════════════════════════════════════════════════════════════════
    // Marketing details fetch (debounced, single useEffect)
    // ═══════════════════════════════════════════════════════════════════════
    const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        if (!isFocused || !cartId || !isAuthenticated) return;

        fetchAppliedCodes({ variables: { cart_id: cartId } });
    }, [isFocused, cartId, isAuthenticated, fetchAppliedCodes]);

    useEffect(() => {
        if (syncStatus !== 'idle' || !cartId || !isFocused || !isAuthenticated) return;

        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            fetchAppliedCodes({ variables: { cart_id: cartId } });
        }, MARKETING_REFETCH_DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [syncStatus, cartId, isFocused, isAuthenticated, fetchAppliedCodes]);

    // ═══════════════════════════════════════════════════════════════════════
    // Refetch applied codes when selected cart items or quantities change
    // Uses a fingerprint of selected references (id:qty pairs) to detect changes.
    // Debounced to avoid excessive API calls during rapid selection/quantity updates.
    // ═══════════════════════════════════════════════════════════════════════
    const selectionFingerprint = useMemo(() => {
        if (!selectedReferences || selectedReferences.length === 0) return '';
        return selectedReferences
            .map(
                (ref: { cartItemReferenceId?: string; qty?: number }) =>
                    `${ref.cartItemReferenceId ?? ''}:${ref.qty ?? 0}`
            )
            .sort()
            .join(',');
    }, [selectedReferences]);

    const isInitialSelectionRenderRef = useRef(true);
    const selectionDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        // Skip initial render (handled by the mount effect above)
        if (isInitialSelectionRenderRef.current) {
            isInitialSelectionRenderRef.current = false;
            return;
        }

        if (!cartId || !isFocused || !isAuthenticated || !selectionFingerprint) return;

        if (selectionDebounceRef.current) clearTimeout(selectionDebounceRef.current);

        selectionDebounceRef.current = setTimeout(() => {
            fetchAppliedCodes({ variables: { cart_id: cartId } });
        }, MARKETING_REFETCH_DEBOUNCE_MS);

        return () => {
            if (selectionDebounceRef.current) clearTimeout(selectionDebounceRef.current);
        };
    }, [selectionFingerprint, cartId, isFocused, isAuthenticated, fetchAppliedCodes]);

    // ═══════════════════════════════════════════════════════════════════════
    // Auto-remove applied code when ALL items are unchecked
    // A coupon/voucher on an empty selection is invalid and causes server errors.
    // ═══════════════════════════════════════════════════════════════════════
    const isRemovingEmptySelectionRef = useRef(false);

    useEffect(() => {
        if (selectedReferenceCount > 0 || !cartId || isRemovingEmptySelectionRef.current) {
            return;
        }

        const voucherId = appliedVouchers?.[0]?.voucher_id;
        const couponCode = appliedCoupons?.[0]?.code;

        if (!voucherId && !couponCode) {
            return;
        }

        isRemovingEmptySelectionRef.current = true;

        const handleRemoveAppliedCode = async () => {
            try {
                if (voucherId) {
                    await removeCode(voucherId, MarketingCodeType.VOUCHER);
                } else if (couponCode) {
                    await removeCode(couponCode, MarketingCodeType.COUPON);
                }

                // Clear all local tracking state so the next apply starts clean
                localTrackedVoucherRef.current = null;
                lastConfirmedCouponDisplayRef.current = '';
                pendingServerVerificationRef.current = false;
                prevMarketingVoucherIdRef.current = null;
                prevMarketingCouponCodeRef.current = null;

                refetchMarketingAppliedCode();
            } catch {
                // Silently ignore — the code will be re-validated on next server call
            } finally {
                isRemovingEmptySelectionRef.current = false;
            }
        };

        handleRemoveAppliedCode();
    }, [selectedReferenceCount, cartId, appliedVouchers, appliedCoupons, removeCode, refetchMarketingAppliedCode]);

    // ═══════════════════════════════════════════════════════════════════════
    // Compute display value from existing data (no API calls)
    // Priority: Pending → Marketing Voucher → Marketing Coupon → Server → Local → Back Buffer
    // ═══════════════════════════════════════════════════════════════════════
    const appliedCoupon = useMemo(() => {
        if (!cartId || selectedReferenceCount === 0) {
            return '';
        }

        // During pending server verification (coupon just applied), show optimistic value
        if (pendingServerVerificationRef.current) {
            if (appliedVouchers && appliedVouchers.length > 0) {
                return '-' + currencyFormatter(appliedVouchers[0]?.applied_amount);
            }
            if (appliedCoupons && appliedCoupons.length > 0) {
                // For coupons, use discount_label if available
                if (appliedCoupons[0]?.discount_label) {
                    return appliedCoupons[0].discount_label;
                }
                // Fallback: show coupon code to keep badge visible while server verifies
                return '';
            }
            // Fallback: show loading indicator
            return '';
        }

        // Priority 1: Marketing voucher data (most detailed, includes applied_amount)
        if (appliedVouchers && appliedVouchers.length > 0) {
            const display = '-' + currencyFormatter(appliedVouchers[0]?.applied_amount);
            lastConfirmedCouponDisplayRef.current = display;
            return display;
        }

        // Priority 2: Marketing coupon data (has code and optional discount_label)
        if (appliedCoupons && appliedCoupons.length > 0) {
            // Use server discount amount for accurate display when available
            if (isServerPrices && serverDiscountAmount != null && serverDiscountAmount !== 0) {
                const display = '-' + currencyFormatter(Math.abs(serverDiscountAmount));
                lastConfirmedCouponDisplayRef.current = display;
                return display;
            }
            // Use discount_label from marketing data
            if (appliedCoupons[0]?.discount_label) {
                const display = appliedCoupons[0].discount_label;
                lastConfirmedCouponDisplayRef.current = display;
                return display;
            }
            // Fallback: show coupon code as badge (truthy value to trigger badge view)
            return '';
        }

        // Priority 3: Server discount amount (when no marketing data available)
        if (isServerPrices && serverDiscountAmount != null && serverDiscountAmount !== 0) {
            const display = '-' + currencyFormatter(Math.abs(serverDiscountAmount));
            lastConfirmedCouponDisplayRef.current = display;
            return display;
        }

        // Priority 4: Local prices discount (during sync/transition)
        if (!isServerPrices) {
            const discountValue = prices?.discounts?.[0]?.amount?.value;
            if (discountValue && discountValue > 0) {
                return '-' + currencyFormatter(discountValue);
            }

            // Use back buffer during transitions to prevent UI flicker
            if (lastConfirmedCouponDisplayRef.current) {
                return lastConfirmedCouponDisplayRef.current;
            }
        }

        // No coupon applied - clear back buffer
        lastConfirmedCouponDisplayRef.current = '';
        return '';
    }, [appliedVouchers, appliedCoupons, cartId, serverDiscountAmount, isServerPrices, prices, selectedReferenceCount]);

    // ═══════════════════════════════════════════════════════════════════════
    // Simplified refetch (only marketing details needed)
    // ═══════════════════════════════════════════════════════════════════════
    const refetchAppliedCode = useCallback(async () => {
        refetchMarketingAppliedCode();
    }, [refetchMarketingAppliedCode]);

    return {
        loading,
        error,
        appliedCoupons,
        appliedVouchers,
        appliedCoupon,
        refetch: refetchAppliedCode,
        cartId,
        isFocused,

        /**
         * Whether no items are currently selected in cart.
         */
        isSelectionEmpty: selectedReferenceCount === 0,

        /**
         * Whether a coupon or voucher is currently applied.
         */
        hasAppliedCode: isServerPrices
            ? !!(serverCouponCode || (serverDiscountAmount != null && serverDiscountAmount !== 0))
            : (appliedVouchers && appliedVouchers.length > 0) || (appliedCoupons && appliedCoupons.length > 0),

        /**
         * Track a voucher/coupon ID locally after applying.
         */
        trackAppliedCode,
    };
};
